import React from 'react';
import { DossierClient, SousDossier, Fichier } from '../utils/types';
import { formatDate, formatFileSize } from '../utils/helpers';
import { canDeleteSubfoldersAndFiles } from '../utils/permissions';

interface SousDossierModalProps {
  isOpen: boolean;
  sousDossier: SousDossier;
  dossier: DossierClient | null;
  currentUserRole: string;
  onClose: () => void;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
}

const SousDossierModal: React.FC<SousDossierModalProps> = ({
  isOpen,
  sousDossier,
  dossier,
  currentUserRole,
  onClose,
  onDownloadFile,
  onDeleteFile,
  onDeleteSousDossier
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl sm:rounded-3xl max-w-full sm:max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border-2 sm:border-4 border-blue-200 mx-2 sm:mx-4">
        <ModalHeader sousDossier={sousDossier} dossier={dossier} onClose={onClose} />
        <ModalContent
          sousDossier={sousDossier}
          dossier={dossier}
          currentUserRole={currentUserRole}
          onDownloadFile={onDownloadFile}
          onDeleteFile={onDeleteFile}
          onDeleteSousDossier={onDeleteSousDossier}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

const ModalHeader: React.FC<{ 
  sousDossier: SousDossier; 
  dossier: DossierClient | null;
  onClose: () => void;
}> = ({ sousDossier, dossier, onClose }) => (
  <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 sm:border-b-4 border-blue-300 bg-gradient-to-r from-blue-600 to-indigo-600">
    <div className="flex-1 min-w-0">
      <h3 className="text-lg sm:text-xl font-semibold text-white truncate">
        {dossier?.typeDocument === 'voyage' ? 'Sous-dossier Voyage' : 'Sous-dossier Transfert'}: {sousDossier.nom}
      </h3>
      <p className="text-blue-100 text-xs sm:text-sm mt-1">
        Créé le {formatDate(sousDossier.date)} | {sousDossier.fichiers.length} fichier(s)
      </p>
    </div>
    <button
      onClick={onClose}
      className="text-white hover:text-gray-200 p-2 hover:bg-white/20 rounded-full transition-all duration-300 flex-shrink-0 ml-2"
    >
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const ModalContent: React.FC<{
  sousDossier: SousDossier;
  dossier: DossierClient | null;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
  onClose: () => void;
}> = ({ sousDossier, dossier, currentUserRole, onDownloadFile, onDeleteFile, onDeleteSousDossier, onClose }) => (
  <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
    <DetailsSection sousDossier={sousDossier} dossier={dossier} />
    <FilesSection
      sousDossier={sousDossier}
      currentUserRole={currentUserRole}
      onDownloadFile={onDownloadFile}
      onDeleteFile={onDeleteFile}
    />
    <ActionsSection
      sousDossier={sousDossier}
      dossier={dossier}
      currentUserRole={currentUserRole}
      onClose={onClose}
      onDeleteSousDossier={onDeleteSousDossier}
    />
  </div>
);

const DetailsSection: React.FC<{ 
  sousDossier: SousDossier; 
  dossier: DossierClient | null;
}> = ({ sousDossier, dossier }) => (
  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
    <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">
      Détails du {dossier?.typeDocument === 'voyage' ? 'voyage' : 'transfert'}
    </h4>
    
    {dossier?.typeDocument === 'voyage' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
        <div>
          <div><span className="font-medium">Pays à visiter:</span> {Array.isArray(sousDossier.pays) ? sousDossier.pays?.join(', ') : sousDossier.pays}</div>
          <div><span className="font-medium">Raison:</span> {sousDossier.raison === 'autres' ? sousDossier.autreRaison : sousDossier.raison}</div>
        </div>
        <div>
          <div><span className="font-medium">Période:</span> Du {sousDossier.dateDebut ? new Date(sousDossier.dateDebut).toLocaleDateString('fr-FR') : 'N/A'} au {sousDossier.dateFin ? new Date(sousDossier.dateFin).toLocaleDateString('fr-FR') : 'N/A'}</div>
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
        <div>
          <div><span className="font-medium">Type de transfert:</span> {dossier?.typeTransfert}</div>
          <div><span className="font-medium">Période:</span> 
            {dossier?.dateDebut && dossier?.dateFin 
              ? `Du ${new Date(dossier.dateDebut).toLocaleDateString('fr-FR')} au ${new Date(dossier.dateFin).toLocaleDateString('fr-FR')}`
              : 'Non spécifiée'
            }
          </div>
        </div>
        <div>
          {sousDossier.motif && (
            <div><span className="font-medium">Détails:</span> {sousDossier.motif}</div>
          )}
        </div>
      </div>
    )}
  </div>
);

const FilesSection: React.FC<{
  sousDossier: SousDossier;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
}> = ({ sousDossier, currentUserRole, onDownloadFile, onDeleteFile }) => (
  <>
    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
      Fichiers ({sousDossier.fichiers.length})
    </h4>

    <div className="space-y-2 sm:space-y-3">
      {sousDossier.fichiers.map((fichier, index) => (
        <FileItem
          key={index}
          fichier={fichier}
          currentUserRole={currentUserRole}
          onDownloadFile={onDownloadFile}
          onDeleteFile={onDeleteFile}
        />
      ))}
    </div>
  </>
);

const FileItem: React.FC<{
  fichier: Fichier;
  currentUserRole: string;
  onDownloadFile: (fileId: string, nomOriginal: string) => void;
  onDeleteFile: (fileId: string, nomOriginal: string, chemin: string) => void;
}> = ({ fichier, currentUserRole, onDownloadFile, onDeleteFile }) => (
  <div className="flex items-center justify-between p-3 sm:p-5 border border-blue-200 rounded-xl bg-gradient-to-r from-blue-50 to-white">
    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
          {fichier.nomOriginal || fichier.nom}
        </p>
        <p className="text-xs text-gray-600">{formatFileSize(fichier.taille)} • {fichier.extension}</p>
        <p className="text-xs text-gray-500 truncate">Fichier système: {fichier.nom}</p>
      </div>
    </div>

    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2">
      <button
        onClick={() => onDownloadFile(fichier._id, fichier.nomOriginal || fichier.nom)}
        className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-2 sm:px-3 py-1 rounded transition-all duration-300 text-xs sm:text-sm"
      >
        Télécharger
      </button>
      {canDeleteSubfoldersAndFiles(currentUserRole) && (
        <button
          onClick={() => onDeleteFile(fichier._id, fichier.nomOriginal || fichier.nom, fichier.chemin)}
          className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 rounded transition-all duration-300 text-xs sm:text-sm"
        >
          Supprimer
        </button>
      )}
    </div>
  </div>
);

const ActionsSection: React.FC<{
  sousDossier: SousDossier;
  dossier: DossierClient | null;
  currentUserRole: string;
  onClose: () => void;
  onDeleteSousDossier: (sousDossier: SousDossier) => void;
}> = ({ sousDossier, dossier, currentUserRole, onClose, onDeleteSousDossier }) => (
  <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t flex justify-end space-x-2 sm:space-x-3">
    <button
      onClick={onClose}
      className="px-3 sm:px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-300 text-sm"
    >
      Fermer
    </button>
    {canDeleteSubfoldersAndFiles(currentUserRole) && dossier?.statut !== 'rejeté' && (
      <button
        onClick={() => onDeleteSousDossier(sousDossier)}
        className="px-3 sm:px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-300 text-sm"
      >
        Supprimer le sous-dossier
      </button>
    )}
  </div>
);

export default SousDossierModal;
